/*

  StatsModal.ts

  Mobile modal for stats and leaderboard

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjálmur Þorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { ts } from "./i18n";
import { ComponentFunc, m } from "./mithril";
import { IView } from "./types";
import { TabBar, ITab } from "./tabbar";
import { StatsView } from "./statsview";
import { LeaderboardView } from "./leaderboardview";
import { glyph } from "./util";

interface IStatsModalAttributes {
  view: IView;
  onClose: () => void;
}

export const StatsModal: ComponentFunc<IStatsModalAttributes> = () => {
  // Component-local state for active tab (defaults to stats)
  let activeTab: string = "stats";

  return {
    view: (vnode) => {
      const { view, onClose } = vnode.attrs;
      const { riddle, state, leaderboard } = view.model;

      if (!riddle) {
        return null;
      }

      // Check if current user is on the leaderboard
      const currentUserId = state?.netskraflUserId || "";
      const isOnLeaderboard = leaderboard && leaderboard.some(
        entry => entry.userId === currentUserId
      );

      const tabs: ITab[] = [
        { id: "stats", label: ts("Tölfræði"), iconGlyph: "stats" },
        {
          id: "leaderboard",
          label: ts("Stigatafla"),
          iconGlyph: "tower",
          badgeGlyph: isOnLeaderboard ? "star" : undefined
        }
      ];

      const handleTabChange = (tabId: string) => {
        activeTab = tabId;
      };

      return [
        // Backdrop
        m(".modal-backdrop-netskrafl", {
          onclick: (e: Event) => { e.preventDefault(); return false;},
          onwheel: (e: Event) => { e.preventDefault(); e.stopPropagation(); return false; },
          ontouchmove: (e: Event) => { e.preventDefault(); e.stopPropagation(); return false; }
        }),

        // Modal dialog
        m(".modal-dialog.stats-modal", {
          onwheel: (e: Event) => { e.stopPropagation(); return false; },
          ontouchmove: (e: Event) => { e.stopPropagation(); return false; }
        }, [
          m(".modal-content", [
            // Close button in top right
            m("button.close", {
              onclick: onClose,
              "aria-label": "Loka"
            }, glyph("remove")),

            // Tab navigation
            m(TabBar, {
              tabs,
              activeTab,
              onTabChange: handleTabChange
            }),

            // Modal body with tab content
            m(".modal-body", [
              activeTab === "stats" ? m(StatsView, {
                stats: view.model.userStats || null,
                loading: false
              }) : null,

              activeTab === "leaderboard" ? m(LeaderboardView, {
                leaderboard: view.model.leaderboard || [],
                currentUserId: state?.netskraflUserId || "",
                date: riddle.date,
                loading: false
              }) : null
            ])
          ])
        ])
      ];
    }
  };
};
